import { useState } from "react";
import { AlertTriangle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface ConflictCell {
  optionId: string;
  optionName: string;
  criterion: string;
  criterionLabel: string;
  phase1Reference: {
    type: 'tension' | 'assumption' | 'risk' | 'success_criteria';
    text: string;
    source: string;
  };
  currentScore: string;
  conflictReason: string;
}

interface ConflictResolutionDialogProps {
  conflict: ConflictCell | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResolve: (conflict: ConflictCell, tradeOffReason: string) => void;
  onChangeValue: (conflict: ConflictCell) => void;
}

export const ConflictResolutionDialog = ({
  conflict,
  open,
  onOpenChange,
  onResolve,
  onChangeValue,
}: ConflictResolutionDialogProps) => {
  const [tradeOffReason, setTradeOffReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAcceptTradeOff = async () => {
    if (!conflict || !tradeOffReason.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onResolve(conflict, tradeOffReason.trim());
      setTradeOffReason("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeValue = () => {
    if (!conflict) return;
    onChangeValue(conflict);
    onOpenChange(false);
    setTradeOffReason("");
  };

  const scoreLabel = conflict?.currentScore === 'high' ? 'Hoch' 
    : conflict?.currentScore === 'medium' ? 'Mittel' 
    : 'Niedrig';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-5 w-5" />
            Widerspruch zu Phase 1
          </DialogTitle>
          <DialogDescription className="pt-1">
            {conflict?.conflictReason}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Phase 1 Reference */}
          <div className="p-3 bg-muted/50 border border-border rounded-lg">
            <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide mb-1">
              {conflict?.phase1Reference.source}
            </p>
            <p className="text-sm italic text-foreground">
              "{conflict?.phase1Reference.text}"
            </p>
          </div>

          {/* Current Value */}
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div>
              <p className="text-sm font-medium">{conflict?.criterionLabel}</p>
              <p className="text-xs text-muted-foreground">
                {conflict?.optionName}
              </p>
            </div>
            <span className={cn(
              "px-2.5 py-1 rounded-md text-sm font-medium",
              conflict?.currentScore === 'high' && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
              conflict?.currentScore === 'medium' && "bg-amber-500/10 text-amber-600 dark:text-amber-400",
              conflict?.currentScore === 'low' && "bg-red-500/10 text-red-600 dark:text-red-400",
            )}>
              {scoreLabel}
            </span>
          </div>

          {/* Trade-off Reason Input */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Begründung für bewussten Trade-off
            </label>
            <Textarea
              placeholder="z.B. 'Das Risiko ist akzeptabel, weil...'"
              value={tradeOffReason}
              onChange={(e) => setTradeOffReason(e.target.value)}
              rows={2}
              className="resize-none"
              autoFocus
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button 
            variant="outline" 
            onClick={handleChangeValue}
            className="flex-1 sm:flex-none"
          >
            Wert ändern
          </Button>
          <Button 
            onClick={handleAcceptTradeOff}
            disabled={!tradeOffReason.trim() || isSubmitting}
            className="flex-1 sm:flex-none gap-1.5"
          >
            <Check className="h-4 w-4" />
            Akzeptieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
